import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import moment, {Moment} from 'moment-timezone'
import React, {createContext, useContext, useMemo} from 'react'

import {hasNewUpdates} from '../utils'

import {useFilteredInstructions} from './api/useFilteredInstructions'
import {PageList, useLastVisited} from './useLastVisited'

export type LastVisitedContext = {
  lastVisited: Moment | undefined
  setLastVisited: () => void
  hasUpdated: boolean
}

export const useInstructionsPageLastVisitedHook = (): LastVisitedContext => {
  const {plantId} = useUpmPlantContext()
  const timezone = useUpmPlantTimezone()
  const {data} = useFilteredInstructions({plantId, timezone, settings: {}})
  const [lastVisited, setLastVisited] = useLastVisited({page: PageList.Instructions})

  const hasUpdated = useMemo(() => {
    if (!lastVisited) return true
    return data?.some(({createdAt, updatedAt}) => {
      const updateVisited =
        (createdAt && hasNewUpdates(moment(createdAt).tz(timezone), lastVisited)) ||
        (updatedAt && hasNewUpdates(moment(updatedAt).tz(timezone), lastVisited))
      return updateVisited
    })
  }, [data, lastVisited, timezone])

  return {
    lastVisited,
    setLastVisited,
    hasUpdated: !!hasUpdated
  }
}

const Context = createContext<LastVisitedContext | undefined>(undefined)

export const InstructionsPageLastVisitedProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const context = useInstructionsPageLastVisitedHook()

  return <Context.Provider value={context}>{children}</Context.Provider>
}

export const useInstructionsPageLastVisited = () => {
  const ctx = useContext(Context)
  if (ctx === undefined) {
    throw new Error(
      'useInstructionsPageLastVisited used outside of InstructionsPageLastVisitedProvider'
    )
  } else {
    return ctx
  }
}
